/**
 * The period and grouping controls for the usage table, both `Select`s: a
 * date-range pair of raw inputs would be two more `<input>`s outside a
 * `FormDialog`, which this plane's raw-element rule forbids, and a filter
 * is not a form -- there is nothing here to submit.
 *
 * The period is a fixed preset, not a free range: `Usage` never persists it
 * (its own doc comment explains why a remembered range is a bug), and a
 * preset needs nothing saved to stay reproducible on the next visit.
 */

import type { ReactNode } from 'react';

import { defineCatalogue } from '../../i18n';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import type { UsageGroupBy } from '../api/queries';

export type UsagePeriod = '7d' | '30d' | '90d';

export interface UsageRange {
  from: string;
  to: string;
}

const PERIOD_DAYS: Record<UsagePeriod, number> = { '7d': 7, '30d': 30, '90d': 90 };

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Computed fresh from `Date.now()` on every call, never read back from anywhere persisted --
 *  the same "always as of now" requirement `Usage`'s own doc comment states for the range. */
export function periodToRange(period: UsagePeriod): UsageRange {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - PERIOD_DAYS[period]);
  return { from: isoDate(from), to: isoDate(to) };
}

const catalogue = defineCatalogue(
  {
    period: 'Período',
    'period.7d': 'Últimos 7 dias',
    'period.30d': 'Últimos 30 dias',
    'period.90d': 'Últimos 90 dias',
    groupBy: 'Agrupar por',
    'groupBy.tenant': 'Por organização',
    'groupBy.day_tenant': 'Por dia e organização',
  },
  {
    period: 'Period',
    'period.7d': 'Last 7 days',
    'period.30d': 'Last 30 days',
    'period.90d': 'Last 90 days',
    groupBy: 'Group by',
    'groupBy.tenant': 'By organisation',
    'groupBy.day_tenant': 'By day and organisation',
  },
);

export function UsageControls(props: {
  period: UsagePeriod;
  groupBy: UsageGroupBy;
  onPeriodChange: (period: UsagePeriod) => void;
  onGroupByChange: (groupBy: UsageGroupBy) => void;
}): ReactNode {
  const t = catalogue.t;
  return (
    <>
      <Select
        value={props.period}
        onValueChange={(value) => props.onPeriodChange(value as UsagePeriod)}
      >
        <SelectTrigger data-testid="usage-period" aria-label={t('period')} className="w-44">
          <SelectValue placeholder={t('period')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="7d">{t('period.7d')}</SelectItem>
          <SelectItem value="30d">{t('period.30d')}</SelectItem>
          <SelectItem value="90d">{t('period.90d')}</SelectItem>
        </SelectContent>
      </Select>
      <Select
        value={props.groupBy}
        onValueChange={(value) => props.onGroupByChange(value as UsageGroupBy)}
      >
        <SelectTrigger data-testid="usage-group-by" aria-label={t('groupBy')} className="w-56">
          <SelectValue placeholder={t('groupBy')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="tenant">{t('groupBy.tenant')}</SelectItem>
          <SelectItem value="day_tenant">{t('groupBy.day_tenant')}</SelectItem>
        </SelectContent>
      </Select>
    </>
  );
}
